<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const columns = [
  { name: 'Menunggu', color: 'secondary', desc: 'Pesanan baru masuk, belum mulai dikerjakan' },
  { name: 'Diproses', color: 'warning', desc: 'Sedang dimasak/diracik oleh tim dapur' },
  { name: 'Siap Diantar', color: 'success', desc: 'Sudah selesai, tinggal diantar ke meja pelanggan' },
]

const timerColors = [
  { color: 'success', label: 'Hijau', desc: 'Pesanan masih baru (di bawah 5 menit)' },
  { color: 'warning', label: 'Kuning', desc: 'Sudah menunggu 5-10 menit — mulai perlu perhatian' },
  { color: 'error', label: 'Merah', desc: 'Sudah lebih dari 10 menit — segera diproses/dicek' },
]

const faq = [
  {
    q: 'Pesanan sudah "Siap Diantar" tapi masih ada di layar, kenapa tidak hilang?',
    a: 'Pesanan hanya hilang dari layar setelah statusnya diubah ke "Selesai/Antar" (klik tombol besar di kartu pesanan). Sampai saat itu, pesanan tetap tampil di kolom "Siap Diantar" sebagai pengingat bahwa makanan sudah jadi.',
  },
  {
    q: 'Kenapa ada tulisan "connecting" atau "disconnected" di pojok layar?',
    a: 'Itu indikator koneksi real-time ke server. "Connected" artinya layar akan update otomatis begitu ada pesanan baru. Kalau tulisannya "disconnected", layar tetap akan me-refresh sendiri tiap 30 detik sebagai cadangan, tapi tidak seinstan biasanya — cek koneksi internet toko Anda.',
  },
  {
    q: 'Bisakah saya buka beberapa layar KDS sekaligus untuk stasiun berbeda?',
    a: 'Bisa. Setiap layar KDS menampilkan 1 stasiun saja. Kalau Anda punya 2 stasiun (misalnya Dapur & Barista), siapkan 2 layar/tablet terpisah, masing-masing dibuka ke stasiun yang berbeda.',
  },
  {
    q: 'Apa gunanya centang per item di dalam satu pesanan?',
    a: 'Kalau satu pesanan berisi beberapa item, Anda bisa menandai item satu per satu selesai dikerjakan (misalnya minuman sudah jadi duluan sebelum makanannya). Menandai item tidak otomatis memindahkan status seluruh pesanan — gunakan tombol besar di bawah kartu untuk itu.',
  },
  {
    q: 'Apakah saya bisa membatalkan pesanan langsung dari layar KDS?',
    a: 'Layar KDS difokuskan untuk memproses pesanan (menunggu → diproses → siap), bukan untuk membatalkan. Pembatalan pesanan dilakukan dari menu kasir/Penjualan.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/f-b-modern" class="text-decoration-none text-medium-emphasis text-body-2">
            F&amp;B Modern
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Kitchen Display</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="error" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-fire-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Kitchen Display (KDS)
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Layar monitor dapur/bar pengganti kertas pesanan — pesanan masuk otomatis, real-time
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Menjelaskan cara membaca dan menggunakan layar Kitchen Display supaya tim dapur/bar
          Anda tahu persis pesanan apa yang harus dikerjakan, tanpa kertas dan tanpa salah catat.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="error" />
            Apa itu Kitchen Display System?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Kitchen Display System (KDS)</strong> adalah layar (bisa pakai tablet,
              monitor bekas, atau laptop) yang dipasang di dapur/bar, menampilkan daftar
              pesanan yang masuk secara otomatis dan langsung update — mirip layar antrean
              di restoran cepat saji, tapi menampilkan detail lengkap tiap pesanan.
            </p>
            <p class="text-body-2 mb-3">
              Begitu ada pesanan baru (baik diinput kasir lewat POS, maupun dipesan sendiri
              oleh pelanggan lewat Self-Order QR), pesanan itu otomatis muncul di layar KDS
              stasiun yang sesuai — tim dapur tidak perlu lagi menerima kertas pesanan
              dari kasir secara manual.
            </p>
            <VAlert type="success" variant="tonal" class="mb-0">
              <strong>Manfaat utama:</strong> mengurangi salah baca tulisan tangan, pesanan
              tidak ada yang terlewat, dan Anda bisa lihat berapa lama sebuah pesanan
              sudah menunggu lewat penanda waktu berwarna.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Buka -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-play-circle-line" class="me-2" color="success" />
            Cara Membuka Layar KDS
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Dari <strong>Dashboard</strong>, cari kartu <strong>"Kitchen Display (KDS)"</strong>
                — akan tampil daftar tombol sesuai stasiun yang sudah Anda buat (lihat panduan
                <RouterLink to="/userguide/f-b-modern/stations">
                  Stasiun Dapur
                </RouterLink> kalau belum ada stasiun).
              </li>
              <li class="mb-3">
                Klik tombol stasiun yang ingin dibuka, misalnya <strong>"Dapur Utama"</strong>.
              </li>
              <li class="mb-3">
                Layar KDS untuk stasiun tersebut akan terbuka, menampilkan 3 kolom pesanan.
              </li>
              <li class="mb-0">
                Klik ikon <strong>layar penuh (fullscreen)</strong> di pojok kanan atas supaya
                tampilan lebih besar dan mudah dilihat dari jauh — cocok untuk dipasang
                permanen di dapur.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- 3 Kolom -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-layout-column-line" class="me-2" color="primary" />
            Membaca 3 Kolom di Layar KDS
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th style="min-width: 140px">Kolom</th>
                  <th>Artinya</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="col in columns" :key="col.name">
                  <td>
                    <VChip :color="col.color" variant="tonal" size="small">
                      {{ col.name }}
                    </VChip>
                  </td>
                  <td class="text-body-2">{{ col.desc }}</td>
                </tr>
              </tbody>
            </VTable>

            <p class="text-body-2 mt-4 mb-2">
              Untuk memindahkan pesanan ke kolom berikutnya, klik tombol besar di bagian
              bawah kartu pesanan:
            </p>
            <VList density="compact">
              <VListItem prepend-icon="ri-arrow-right-line">
                <VListItemTitle class="text-body-2">
                  Di kolom <strong>Menunggu</strong> → tombol <strong>"Mulai Proses"</strong>
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-line">
                <VListItemTitle class="text-body-2">
                  Di kolom <strong>Diproses</strong> → tombol <strong>"Tandai Siap"</strong>
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-arrow-right-line">
                <VListItemTitle class="text-body-2">
                  Di kolom <strong>Siap Diantar</strong> → tombol <strong>"Selesai / Antar"</strong>
                  (pesanan akan hilang dari layar setelah ini)
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Timer warna -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-time-line" class="me-2" color="warning" />
            Arti Warna Penanda Waktu di Setiap Kartu Pesanan
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setiap kartu pesanan punya label waktu yang berubah warna otomatis,
              menunjukkan sudah berapa lama pesanan itu menunggu sejak masuk:
            </p>
            <VList density="compact">
              <VListItem v-for="t in timerColors" :key="t.color" :prepend-icon="'ri-checkbox-blank-circle-fill'">
                <template #prepend>
                  <VIcon icon="ri-checkbox-blank-circle-fill" :color="t.color" size="16" class="me-2" />
                </template>
                <VListItemTitle class="text-body-2">
                  <strong>{{ t.label }}</strong> — {{ t.desc }}
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert type="error" variant="tonal" icon="ri-error-warning-line">
          <strong>⚠️ PENTING:</strong>
          Layar KDS membutuhkan koneksi internet yang stabil supaya pesanan baru muncul
          secara instan. Kalau koneksi toko sering putus-nyambung, layar tetap akan
          memperbarui diri secara otomatis setiap 30 detik sebagai jaring pengaman,
          tapi sebaiknya tetap cek koneksi wifi/internet dapur Anda secara berkala.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Pasang tablet/monitor permanen di dapur</strong> yang selalu
                  menyala, dan aktifkan mode layar penuh (fullscreen) supaya tim dapur
                  bisa lihat dari jarak agak jauh tanpa perlu sentuh layar terus-menerus.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Perhatikan warna merah</strong> pada kartu pesanan — itu tanda
                  pesanan sudah menunggu cukup lama dan perlu segera diproses supaya
                  pelanggan tidak menunggu terlalu lama.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Kalau ada beberapa stasiun</strong>, siapkan 1 layar/tablet
                  per stasiun agar masing-masing tim (dapur, barista, dessert) tidak
                  bingung melihat pesanan tim lain.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Stasiun Dapur', sub: 'Atur pembagian stasiun sebelum pakai KDS', icon: 'ri-restaurant-2-line', color: 'info', to: '/userguide/fb-modern/stations' },
              { title: 'Self-Order QR Meja', sub: 'Sumber pesanan otomatis dari pelanggan', icon: 'ri-qr-scan-2-line', color: 'primary', to: '/userguide/fb-modern/self-order' },
              { title: 'Pengaturan Printer', sub: 'Cetak struk dapur otomatis per stasiun', icon: 'ri-printer-line', color: 'secondary', to: '/userguide/sistem/printers' },
              { title: 'Sistem Antrian', sub: 'Status antrian otomatis begitu semua tiket dapur selesai', icon: 'ri-hashtag', color: 'success', to: '/userguide/operasional/sistem-antrian' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
